import { useMemo } from 'react';
import {
  ProductType,
  type addToCartArgsType,
} from 'components/shared/Cart/types';
import { tagBundle } from 'components/shared/Cart/bundle';
import { useBuyContext } from './BuyContext';
import {
  cardHasOptions,
  resolve,
  type CardConfig,
  type ResolveCtx,
  type SelectionsMap,
} from './cardSchema';

const walkLineItems = (
  cards: CardConfig[],
  selections: SelectionsMap,
  ctx: ResolveCtx,
  out: addToCartArgsType[],
): void => {
  for (const card of cards) {
    if (card.kind === 'subsection') {
      walkLineItems([card.child], selections, ctx, out);
      continue;
    }
    if (card.kind === 'composite') {
      walkLineItems(card.children, selections, ctx, out);
      continue;
    }
    if (card.kind === 'actionCard') continue;
    if (!cardHasOptions(card)) continue;

    const selected = selections[card.id];
    if (selected == null) continue;
    const selectedIds = Array.isArray(selected) ? selected : [selected];

    for (const optionId of selectedIds) {
      const option = card.options.find((o) => o.id === optionId);
      if (!option) continue;

      if (option.toCartItem) {
        out.push(...option.toCartItem(ctx));
        continue;
      }

      const variantId = resolve(option.variantId, ctx);
      if (!variantId) continue;
      out.push({
        shopifyVariantId: variantId,
        productType: ProductType.GENERIC,
      });
    }
  }
};

export const useBuyLineItems = (): addToCartArgsType[] => {
  const { cards, selections, baseVariantId, resolveCtx } = useBuyContext();

  return useMemo(() => {
    const items: addToCartArgsType[] = [];
    walkLineItems(cards, selections, resolveCtx, items);

    if (baseVariantId) {
      items.push({
        shopifyVariantId: baseVariantId,
        productType: ProductType.GENERIC,
      });
    }

    return tagBundle(items);
  }, [cards, selections, baseVariantId, resolveCtx]);
};
